<script setup lang="ts">
import { z } from 'zod'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import { useToast } from '@/shared/composables/UseToast'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'
import SubscriptionInputSelect from '@/shared/components/form/SubscriptionInputSelect.vue'
import SalesDemoSourceFields from '@/features/sales/components/SalesDemoSourceFields.vue'
import {
  useAdminCreateSalesDemo,
  useAdminListMyLinkableSalesInvites,
  getAdminListSalesDailyLogsQueryKey,
  getAdminListMySalesDailyLogsQueryKey,
  getAdminGetSalesStatsQueryKey,
  getAdminGetMySalesStatsQueryKey,
  getAdminListSalesDemosQueryKey,
  getAdminListMyLinkableSalesInvitesQueryKey,
} from '@swipe4work/api-orval'
import type {
  ListLinkableSalesInvitesLinkableInviteDto,
  SalesDemoPotentialSubscriptionType,
  SalesDemoSource,
  SalesDemoStatus,
} from '@swipe4work/models-orval'
import { getStatusLabel, getStatusSeverity, isCommittedStatus, statusOptions } from '@/features/sales/composables/useSalesMetricsAdapter'
import { useQueryClient } from '@tanstack/vue-query'

const toast = useToast()
const { closeDialog } = useDynamicDialogLifecycle()
const queryClient = useQueryClient()

const { mutateAsync: createDemo, isPending } = useAdminCreateSalesDemo()

type DemoSubscriptionType = SalesDemoPotentialSubscriptionType | 'None'

const date = ref<Date>(new Date())
const status = ref<SalesDemoStatus>('Planned')
const companyName = ref('')
const contactFirstName = ref('')
const contactLastName = ref('')
const contactEmail = ref('')
const demoDate = ref<Date | null>(null)
const potentialCloseDate = ref<Date | null>(null)
const source = ref<SalesDemoSource | null>(null)
const sourceOther = ref('')
const subscriptionType = ref<DemoSubscriptionType>('None')
const matchCredits = ref<number | null>(null)
const subscriptionId = ref<number | null>(null)

// Contact: either enter a new contact person, or link an already-sent invite that has no demo yet.
type ContactMode = 'new' | 'invite'
const contactMode = ref<ContactMode>('new')
const selectedInviteId = ref<number | null>(null)

/*
 * Deal won is left out: it follows from the company actually being created, never from an entry.
 * Deal lost needs a reason, which belongs in the status dialog.
 */
const demoStatusOptions = statusOptions.filter(o => o.value !== 'DealWon' && o.value !== 'DealLost')

// A written agreement carries the forecast, so it may not be entered without a deal attached.
const requiresDeal = computed(() => isCommittedStatus(status.value))

const contactModeOptions: {
  label: string,
  value: ContactMode
}[] = [
  {
    label: 'Nieuw contactpersoon',
    value: 'new',
  },
  {
    label: 'Bestaande invite koppelen',
    value: 'invite',
  },
]

const { data: linkableInvitesData, isPending: isLoadingInvites } = useAdminListMyLinkableSalesInvites()
const linkableInvites = computed<ListLinkableSalesInvitesLinkableInviteDto[]>(() => linkableInvitesData.value?.invites ?? [])

function inviteName(invite: ListLinkableSalesInvitesLinkableInviteDto): string {
  return [ invite.firstName, invite.lastName ].filter(Boolean).join(' ').trim() || invite.email
}

const formatInviteDate = (d: Date | string | null | undefined) => {
  if (!d) return '—'

  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(d))
}

function toDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const schema = z.object({
  companyName: z.string({ required_error: 'Bedrijfsnaam is verplicht' }).min(1, 'Bedrijfsnaam is verplicht'),
  contactEmail: z.string().email('Ongeldig e-mailadres').or(z.literal('')).nullish(),
})

const resolver = zodResolver(schema)

const sourceError = ref('')
const sourceOtherError = ref('')

const subscriptionTypeOptions = [
  {
    label: 'Geen',
    value: 'None',
  },
  {
    label: 'Match Credits',
    value: 'MatchCredits',
  },
  {
    label: 'Vacature Slots',
    value: 'VacancySlots',
  },
]

async function onSubmit({ valid }: {
  valid: boolean
}) {
  if (!valid) return

  const linkInvite = contactMode.value === 'invite'
  if (linkInvite && !selectedInviteId.value) {
    toast.error('Fout', 'Selecteer een uitnodiging om te koppelen')

    return
  }

  if (!source.value) {
    sourceError.value = 'Bron is verplicht'

    return
  }
  if (source.value === 'Other' && !sourceOther.value.trim()) {
    sourceOtherError.value = 'Vul in via welk kanaal de demo binnenkwam'

    return
  }
  sourceError.value = ''
  sourceOtherError.value = ''

  if (requiresDeal.value && (subscriptionType.value === 'None' || !potentialCloseDate.value)) {
    toast.error(
      'Fout',
      `Een potentieel abonnement en onboarding datum zijn verplicht bij status ${getStatusLabel(status.value)}`,
    )

    return
  }

  try {
    await createDemo({
      data: {
        date: toDateOnly(date.value) as unknown as Date,
        companyName: companyName.value.trim(),
        contactFirstName: linkInvite ? null : contactFirstName.value,
        contactLastName: linkInvite ? null : contactLastName.value,
        contactEmail: linkInvite ? null : contactEmail.value,
        demoDate: demoDate.value ?? null,
        potentialCloseDate: potentialCloseDate.value ?? null,
        source: source.value,
        sourceOther: source.value === 'Other' ? sourceOther.value.trim() : null,
        potentialSubscriptionType: subscriptionType.value === 'None' ? undefined : subscriptionType.value,
        potentialSubscriptionId: subscriptionType.value === 'VacancySlots' ? subscriptionId.value : null,
        potentialMatchCredits: subscriptionType.value === 'MatchCredits' ? matchCredits.value : null,
        employerInviteId: linkInvite ? selectedInviteId.value : null,
        status: status.value,
      },
    })

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListMySalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetSalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetMySalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDemosQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListMyLinkableSalesInvitesQueryKey() }),
    ])

    toast.success('Demo aangemaakt', 'De demo is succesvol aangemaakt')
    closeDialog()
  }
  catch (error: any) {
    const detail = error?.response?.data?.detail
      ?? error?.data?.detail
      ?? 'Er is een fout opgetreden bij het aanmaken van de demo'

    toast.error('Fout', detail)
  }
}
</script>

<template>
  <section class="max-h-screen min-w-lg flex flex-col gap-4 overflow-y-auto">
    <div class="mt-5 flex flex-col gap-1">
      <h2>
        Nieuwe Demo
      </h2>
      <p class="text-sm text-surface-500">
        Voeg een demo toe aan je log van de gekozen dag (of maak een nieuwe log aan).
      </p>
    </div>

    <Form :resolver="resolver" :default-values="{ companyName, contactEmail }" @submit="onSubmit" class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2 flex flex-col gap-1">
          <label for="demoLogDate">Datum log</label>
          <DatePicker
            v-model="date"
            input-id="demoLogDate"
            date-format="dd-mm-yy"
            show-button-bar
            class="w-full"
          />
        </div>

        <div class="col-span-2 flex flex-col gap-1">
          <label for="demoStatus">Status</label>
          <Select
            v-model="status"
            input-id="demoStatus"
            :options="demoStatusOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          >
            <template #value="{ value }">
              <Tag v-if="value" :value="getStatusLabel(value)" :severity="getStatusSeverity(value)" />
            </template>
            <template #option="{ option }">
              <Tag :value="option.label" :severity="option.severity" />
            </template>
          </Select>
        </div>

        <FormField v-slot="$field" name="companyName" class="col-span-2 flex flex-col gap-1">
          <label for="companyName">Bedrijfsnaam</label>
          <InputText
            v-bind="$field.props"
            v-model="companyName"
            input-id="companyName"
            class="w-full"
            :invalid="$field?.invalid"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <div class="col-span-2 flex flex-col gap-1">
          <label for="contactMode">Contactpersoon</label>
          <SelectButton
            v-model="contactMode"
            input-id="contactMode"
            :options="contactModeOptions"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            class="w-full"
          />
        </div>

        <template v-if="contactMode === 'new'">
          <div class="flex flex-col gap-1">
            <label for="firstName">Voornaam (optioneel)</label>
            <InputText
              id="firstName"
              v-model="contactFirstName"
              input-id="firstName"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="lastName">Achternaam (optioneel)</label>
            <InputText
              id="lastName"
              v-model="contactLastName"
              input-id="lastName"
              class="w-full"
            />
          </div>

          <FormField v-slot="$field" name="contactEmail" class="col-span-2 flex flex-col gap-1">
            <label for="contactEmail">E-mailadres (optioneel)</label>
            <InputText
              v-bind="$field.props"
              v-model="contactEmail"
              input-id="contactEmail"
              class="w-full"
              :invalid="$field?.invalid"
            />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
              {{ $field.error?.message }}
            </Message>
          </FormField>
        </template>

        <template v-else>
          <div v-if="isLoadingInvites" class="col-span-2 flex flex-col gap-2">
            <Skeleton v-for="n in 3" :key="n" height="3rem" />
          </div>
          <div
            v-else-if="linkableInvites.length === 0"
            class="col-span-2 border border-surface-200 rounded-md border-dashed p-6 text-center"
          >
            <span class="text-sm text-surface-400">Geen koppelbare uitnodigingen gevonden.</span>
          </div>
          <Listbox
            v-else
            v-model="selectedInviteId"
            :options="linkableInvites"
            option-label="email"
            option-value="id"
            class="col-span-2 w-full"
            list-style="max-height: 16rem"
          >
            <template #option="{ option }">
              <div class="flex flex-col gap-0.5">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium">{{ inviteName(option) }}</span>
                  <Tag
                    v-if="option.companyCreatedAt"
                    value="Bedrijf aangemaakt"
                    severity="success"
                  />
                </div>
                <span class="text-xs text-surface-400">{{ option.email }}</span>
                <span class="text-xs text-surface-400">Verstuurd: {{ formatInviteDate(option.createdAt) }}</span>
              </div>
            </template>
          </Listbox>
        </template>

        <div class="flex flex-col gap-1">
          <label for="demoDate">Demo datum (optioneel)</label>
          <DatePicker
            v-model="demoDate"
            input-id="demoDate"
            date-format="dd-mm-yy"
            show-button-bar
            show-clear
            class="w-full"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label for="closeDate">
            Potentiële onboarding datum{{ requiresDeal ? '' : ' (optioneel)' }}
          </label>
          <DatePicker
            v-model="potentialCloseDate"
            input-id="closeDate"
            date-format="dd-mm-yy"
            show-button-bar
            show-clear
            class="w-full"
          />
        </div>

        <SalesDemoSourceFields
          v-model:source="source"
          v-model:source-other="sourceOther"
          :source-error="sourceError"
          :source-other-error="sourceOtherError"
        />

        <div class="col-span-2 flex flex-col gap-1">
          <label for="subType">Potentieel abonnement</label>
          <SelectButton
            v-model="subscriptionType"
            input-id="subType"
            :options="subscriptionTypeOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>

        <div v-if="subscriptionType === 'MatchCredits'" class="col-span-2 flex flex-col gap-1">
          <label for="matchCredits">Aantal match credits</label>
          <InputNumber
            v-model="matchCredits"
            input-id="matchCredits"
            :min="0"
            class="w-full"
          />
        </div>

        <div v-if="subscriptionType === 'VacancySlots'" class="col-span-2 flex flex-col gap-1">
          <label for="subscription">Abonnement</label>
          <SubscriptionInputSelect
            v-model="subscriptionId"
            input-id="subscription"
          />
        </div>
      </div>

      <div class="mt-2 flex justify-end gap-2">
        <Button label="Annuleren" severity="secondary" :disabled="isPending" @click="closeDialog" />
        <Button type="submit" label="Opslaan" :loading="isPending" />
      </div>
    </Form>
  </section>
</template>
